import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";

import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import InputAdornment from "@mui/material/InputAdornment";
import Event from "@mui/icons-material/Event";
import { makeStyles } from "@mui/styles";

import { Dialog, DatePicker, TimePicker, SelectField } from "orion-components/CBComponents";
import { Translate, getTranslation } from "orion-components/i18n";
import DisplayTypes from "./DisplayTypes";
import { createReminder, updateReminder, deleteReminder } from "../reminders/actions";
import { getDelayFromDuration } from "../../../SharedComponents/trans-utils";

const useStyles = makeStyles({
	notchedOutline: {
		borderColor: "rgba(255, 255, 255, 0.23)"
	}
});

const regexp = /^\d*$/;
const durationOptions = ["m", "h", "d"];
const initialState = {
	displayType: "none",
	title: "",
	description: "",
	dueDate: null,
	recurring: {
		enabled: false,
		value: null,
		unit: "h"
	},
	preNotification: {
		enabled: false,
		value: null,
		unit: "h"
	}
};

const ReminderDialog = props => {
	const classes = useStyles();
	const dispatch = useDispatch();

	const { open, closeDialog, dir } = props;
	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);
	const editMode = props.reminder;

	const [reminder, setReminder] = useState(initialState);
	const [errors, setErrors] = useState(null);

	const handleChange = (key, value) => {
		const update = { ...reminder };
		update[key] = value;
		// If a prenotification value greater than 0 is et then enable it
		if (key === "preNotification") {
			update[key].enabled = typeof value.value === "number" && value.value > 0;
		}
		if (key === "dueDate") {
			if (value instanceof Date && !isNaN(value)) {
				update[key].setSeconds(0, 0);
			}
		}
		setReminder(update);
	};

	useEffect(() => {
		if (props.reminder) setReminder(props.reminder);
	}, [props.reminder]);

	const styles = {
		label: {
			fontSize: 10,
			color: "#B4B8BC",
			marginBottom: 5
		},
		spacer: {
			width: 10
		},
		pickers: {
			display: "flex",
			justifyContent: "space-between",
			marginBottom: 10
		},
		menuPaperProps: {
			maxHeight: 150,
			overflowY: "auto",
			maxWidth: 90
		},
		before: {
			color: "#B4B8BC",
			fontSize: 10,
			margin: "0 10px",
			height: 30,
			display: "flex",
			alignItems: "center"
		},
		error: {
			color: "#d32f2f",
			margin: "unset",
			fontSize: 10
		}
	};

	const dateTimePickerStyles = {
		style: { margin: 0, height: 30, width: "100%" },
		customStyles: {
			label: styles.label,
			labelFocused: styles.label,
			fieldset: {
				width: "auto",
				borderColor: "rgba(255, 255, 255, 0.23)"
			},
			fieldsetHover: {
				borderColor: "rgba(255, 255, 255, 0.23)"
			},
			fieldsetFocused: {
				borderColor: "rgba(255, 255, 255, 0.23)"
			},
			inputRoot: {
				height: 30,
				padding: "0px 10px!important"
			},
			outlinedInput: {
				fontSize: 10,
				color: "#fff"
			}
		}
	};

	const validateReminder = () => {
		const now = new Date();
		const dueDate =
			reminder.dueDate instanceof Date ? reminder.dueDate.getTime() : new Date(reminder.dueDate).getTime();

		if (dueDate <= now) {
			setErrors({ dueDate: getTranslation("global.dock.notificationsCenter.reminderDialog.errors.dueDate") });
			return false;
		} else if (reminder.recurring?.enabled && reminder.preNotification?.enabled) {
			const preDuration = getDelayFromDuration(reminder.preNotification.value, reminder.preNotification.unit);
			const recurringDuration = getDelayFromDuration(reminder.recurring.value, reminder.recurring.unit);
			if (preDuration >= recurringDuration) {
				setErrors({
					preNotification: getTranslation(
						"global.dock.notificationsCenter.reminderDialog.errors.preNotification"
					)
				});
				return false;
			}
		}
		return true;
	};

	const resetDialog = () => {
		setErrors(null);
		setReminder(initialState);
		closeDialog();
	};

	return (
		<Dialog
			open={open}
			title={
				<h3>
					<Translate
						value={`global.dock.notificationsCenter.reminderDialog.${editMode ? "editReminder" : "addReminder"}`}
					/>
				</h3>
			}
			textContent={getTranslation(
				`global.dock.notificationsCenter.reminderDialog.${editMode ? "editReminderText" : "addReminderText"}`
			)}
			paperPropStyles={{
				backgroundColor: "#262729",
				padding: "15px",
				width: 600
			}}
			dialogContentStyles={{
				padding: "0 12px"
			}}
			abort={{
				label: getTranslation("global.dock.notificationsCenter.reminderDialog.cancel"),
				action: () => resetDialog()
			}}
			confirm={{
				label: getTranslation(`global.dock.notificationsCenter.reminderDialog.${editMode ? "save" : "create"}`),
				action: () => {
					if (!validateReminder()) return;
					editMode ? dispatch(updateReminder(reminder.id, reminder)) : dispatch(createReminder(reminder));
					resetDialog();
				},
				disabled: isEmpty(reminder.title) || !reminder.dueDate
			}}
			dismiss={
				editMode
					? {
							label: getTranslation(`global.dock.notificationsCenter.reminderDialog.dismiss`),
							action: () => {
								dispatch(deleteReminder(reminder.id));
								resetDialog();
							},
							disabled: isEmpty(reminder.id),
							style: { color: "#fff", background: "#4CB5F4" }
						}
					: null
			}
		>
			<Fragment>
				<DisplayTypes
					setDisplayType={displayType => handleChange("displayType", displayType)}
					displayType={reminder.displayType}
				/>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						marginTop: 10,
						marginBottom: 30
					}}
				>
					<div style={{ marginBottom: 10 }}>
						<Typography style={styles.label}>
							<Translate value="global.dock.notificationsCenter.reminderDialog.taskName" />
						</Typography>
						<TextField
							variant="outlined"
							placeholder={getTranslation("global.dock.notificationsCenter.reminderDialog.enterName")}
							value={reminder.title}
							onChange={e => handleChange("title", e.target.value)}
							// error={error[app.appId]}
							// helperText={error[app.appId]}
							style={{
								width: "100%",
								margin: "auto"
							}}
							InputProps={{
								style: {
									padding: "0px 10px",
									height: 30,
									color: "#fff",
									display: "block"
								},
								classes: {
									notchedOutline: classes.notchedOutline
								}
							}}
							inputProps={{
								style: {
									padding: 0,
									fontSize: 12,
									height: 30
								}
							}}
						/>
					</div>
					<div style={{ marginBottom: 10 }}>
						<TextField
							variant="outlined"
							placeholder={getTranslation("global.dock.notificationsCenter.reminderDialog.description")}
							value={reminder.description}
							onChange={e => handleChange("description", e.target.value)}
							// error={error[app.appId]}
							// helperText={error[app.appId]}
							style={{
								width: "100%",
								margin: "auto"
							}}
							InputProps={{
								style: {
									padding: "0px 10px",
									height: 30,
									color: "#fff",
									display: "block"
								},
								classes: {
									notchedOutline: classes.notchedOutline
								}
							}}
							inputProps={{
								style: {
									padding: 0,
									fontSize: 12,
									height: 30
								}
							}}
						/>
					</div>
					<div style={styles.pickers}>
						<div style={{ display: "flex", flexDirection: "column", width: "50%" }}>
							<Typography style={styles.label}>
								<Translate value="global.dock.notificationsCenter.reminderDialog.date" />
							</Typography>
							<DatePicker
								id="date"
								key="date"
								value={reminder.dueDate}
								handleChange={date => handleChange("dueDate", date)}
								dir={dir}
								useCustomTextField={true}
								options={{
									placeholder: getTranslation(
										"global.dock.notificationsCenter.reminderDialog.selectDate"
									),
									InputProps: {
										startAdornment: (
											<InputAdornment position="start">
												<Event style={{ fontSize: 18, color: "#fff" }} />
											</InputAdornment>
										)
									},
									error: errors?.dueDate,
									...dateTimePickerStyles
								}}
							/>
						</div>
						<div style={styles.spacer} />
						<div style={{ display: "flex", flexDirection: "column", width: "50%" }}>
							<Typography style={styles.label}>
								<Translate value="global.dock.notificationsCenter.reminderDialog.time" />
							</Typography>
							<TimePicker
								id="time"
								key="time"
								value={reminder.dueDate}
								handleChange={time => handleChange("dueDate", time)}
								format={`time_${timeFormatPreference}`}
								dir={dir}
								useCustomTextField={true}
								options={{
									placeholder: getTranslation(
										"global.dock.notificationsCenter.reminderDialog.setTime"
									),
									error: errors?.dueDate,
									...dateTimePickerStyles
								}}
							/>
						</div>
					</div>
					{errors?.dueDate && <p style={styles.error}>{errors?.dueDate}</p>}
					<div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
						<Typography style={{ color: "#B4B8BC", fontSize: 10 }}>
							<Translate value={"global.dock.notificationsCenter.reminderDialog.recurring"} />
						</Typography>
						<Switch
							checked={reminder.recurring?.enabled}
							color="primary"
							onChange={e =>
								handleChange("recurring", { ...reminder.recurring, enabled: e.target.checked })
							}
							sx={{ margin: "0 20px" }}
						/>
					</div>
					{reminder.recurring?.enabled && (
						<div style={{ marginBottom: 10, display: "flex", alignItems: "flex-end" }}>
							<div style={{ display: "flex", flexDirection: "column", width: "15%" }}>
								<Typography style={styles.label}>
									<Translate value="global.dock.notificationsCenter.reminderDialog.repeatEvery" />
								</Typography>
								<TextField
									variant="outlined"
									placeholder={"00"}
									value={reminder.recurring?.value || ""} // Using an empty string as a fallback to avoid uncontrolled behavior(where the input is handled by the DOM and not by React state).
									onChange={e => {
										const value = Number(e.target.value);
										if (regexp.test(value))
											handleChange("recurring", { ...reminder.recurring, value });
									}}
									InputProps={{
										style: {
											padding: "0px 10px",
											height: 30,
											color: "#fff",
											display: "block"
										},
										classes: {
											notchedOutline: classes.notchedOutline
										}
									}}
									inputProps={{
										maxLength: 2,
										style: {
											padding: 0,
											fontSize: 12,
											height: 30
										}
									}}
								/>
							</div>
							<div style={styles.spacer} />
							<SelectField
								id="recurring_unit"
								key="recurring_unit"
								value={reminder.recurring?.unit}
								handleChange={e =>
									handleChange("recurring", { ...reminder.recurring, unit: e.target.value })
								}
								dir={dir}
								inline
								variant="outlined"
								formControlProps={{
									style: { margin: 0, width: "25%" }
								}}
								minimizedSelect={true}
								menuPaperProps={styles.menuPaperProps}
								fieldSetBorderColor={"rgba(255, 255, 255, 0.23)"}
							>
								{durationOptions.map(option => {
									return (
										<MenuItem key={option} value={option} style={{ display: "flex" }}>
											<Translate
												value={`global.dock.notificationsCenter.reminderDialog.${option}`}
											/>
										</MenuItem>
									);
								})}
							</SelectField>
						</div>
					)}
					<div style={{ marginBottom: 10, display: "flex", alignItems: "flex-end" }}>
						<div style={{ display: "flex", flexDirection: "column", width: "15%" }}>
							<Typography style={styles.label}>
								<Translate value="global.dock.notificationsCenter.reminderDialog.notifyMe" />
							</Typography>
							<TextField
								variant="outlined"
								placeholder={"00"}
								value={reminder.preNotification?.value || ""} // Using an empty string as a fallback to avoid uncontrolled behavior(where the input is handled by the DOM and not by React state).
								onChange={e => {
									const value = Number(e.target.value);
									if (regexp.test(value))
										handleChange("preNotification", { ...reminder.preNotification, value });
								}}
								InputProps={{
									style: {
										padding: "0px 10px",
										height: 30,
										color: "#fff",
										display: "block"
									},
									classes: {
										notchedOutline: classes.notchedOutline
									}
								}}
								inputProps={{
									maxLength: 2,
									style: {
										padding: 0,
										fontSize: 12,
										height: 30
									}
								}}
								error={errors?.preNotification}
							/>
						</div>
						<div style={styles.spacer} />
						<SelectField
							id="preNotification_unit"
							key="preNotification_unit"
							value={reminder.preNotification?.unit}
							handleChange={e =>
								handleChange("preNotification", { ...reminder.preNotification, unit: e.target.value })
							}
							dir={dir}
							inline
							variant="outlined"
							formControlProps={{
								style: { margin: 0, width: "25%" }
							}}
							minimizedSelect={true}
							menuPaperProps={styles.menuPaperProps}
							fieldSetBorderColor={"rgba(255, 255, 255, 0.23)"}
						>
							{durationOptions.map(option => {
								return (
									<MenuItem key={option} value={option} style={{ display: "flex" }}>
										<Translate value={`global.dock.notificationsCenter.reminderDialog.${option}`} />
									</MenuItem>
								);
							})}
						</SelectField>
						<Typography style={styles.before}>
							<Translate value="global.dock.notificationsCenter.reminderDialog.before" />
						</Typography>
					</div>
					{errors?.preNotification && <p style={styles.error}>{errors?.preNotification}</p>}
				</div>
			</Fragment>
		</Dialog>
	);
};

export default ReminderDialog;
